import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { StatusChip, STATUS_LABELS, fmtDateTime } from "./_shared";
import AttachmentPicker from "../../components/attachments/AttachmentPicker";
import AttachmentBubble from "../../components/attachments/AttachmentBubble";
import { postMessageWithOptionalFiles } from "../../components/attachments/postMessage";

const ALLOWED = {
    aperto: ["in_lavorazione", "chiuso"],
    in_lavorazione: ["in_attesa", "chiuso", "aperto"],
    in_attesa: ["in_lavorazione", "chiuso"],
    chiuso: ["in_lavorazione"],
};

export default function TicketDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: authUser } = useAuth();
    const [t, setT] = useState(null);
    const [text, setText] = useState("");
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [setStatus, setSetStatus] = useState(null); // "keep" | status string
    const [users, setUsers] = useState([]);
    const [cats, setCats] = useState([]);
    const [statusDraft, setStatusDraft] = useState("");
    const [assigneeDraft, setAssigneeDraft] = useState("");
    const [catDraft, setCatDraft] = useState("");
    const [editCat, setEditCat] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/internal/tickets/${id}`);
            setT(data);
            setStatusDraft(data.status);
            setAssigneeDraft(data.assignee?.id || "");
            setCatDraft(data.category?.id || "");
            setSetStatus(null);
        } catch { toast.error("Ticket non trovato"); navigate("/app/ticket"); }
    }, [id, navigate]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/internal/internal-users", { params: { active: true } }).then(({ data }) => setUsers(data.items || []));
        api.get("/internal/ticket-categories", { params: { active: "true" } }).then(({ data }) => setCats(data.items || []));
    }, []);

    if (!t) return <div style={{ padding: 40, color: "#8A8F88" }}>Caricamento…</div>;

    const reply = async (e) => {
        e.preventDefault();
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            const extra = {};
            if (setStatus && setStatus !== "keep") extra.set_status = setStatus;
            await postMessageWithOptionalFiles(`/internal/tickets/${id}/messages`, {
                text: text.trim(), files, extra,
            });
            setText(""); setFiles([]); setSetStatus(null);
            await load(); toast.success("Risposta pubblicata");
        } catch (err) {
            if (err?.response?.status === 429) toast.error("Troppi messaggi in poco tempo — riprova tra un minuto");
            else toast.error(err?.response?.data?.detail || "Errore");
        } finally { setSending(false); }
    };
    const changeStatus = async () => {
        if (statusDraft === t.status) return;
        try { await api.post(`/internal/tickets/${id}/status`, { status: statusDraft }); toast.success("Stato aggiornato"); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };
    const assign = async () => {
        const aid = assigneeDraft || null;
        if ((t.assignee?.id || "") === (aid || "")) return;
        try { await api.post(`/internal/tickets/${id}/assign`, { assigned_internal_user_id: aid }); toast.success("Assegnazione aggiornata"); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };
    const saveCat = async () => {
        if (catDraft === t.category?.id) { setEditCat(false); return; }
        try { await api.patch(`/internal/tickets/${id}`, { category_id: catDraft }); toast.success("Categoria aggiornata"); setEditCat(false); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };

    const isSuperAdmin = !!authUser?.is_super_admin;
    const canDelete = isSuperAdmin && t.status === "chiuso";
    const doDelete = async () => {
        if (deleting) return;
        setDeleting(true);
        try {
            await api.delete(`/internal/tickets/${id}`);
            toast.success("Ticket eliminato");
            navigate("/app/ticket");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Errore durante l'eliminazione";
            toast.error(detail);
            setDeleting(false);
            setConfirmDelete(false);
        }
    };

    const isChiuso = t.status === "chiuso";
    const radioOpts = isChiuso
        ? [{ k: "keep", l: "Aggiungi nota (resta chiuso)" }, { k: "in_lavorazione", l: "Rispondi e riapri" }]
        : [
            { k: "keep", l: "Mantieni stato" },
            { k: "in_lavorazione", l: "Rispondi e mantieni in lavorazione" },
            { k: "in_attesa", l: "Rispondi e metti in attesa cliente" },
            { k: "chiuso", l: "Rispondi e chiudi" },
        ];
    const allowedNext = ALLOWED[t.status] || [];

    return (
        <div style={{ padding: "32px 40px" }} data-testid="ticket-detail-page">
            <Link to="/app/ticket" className="lt-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 20 }}>
                <ArrowLeft size={14} /> Torna ai ticket
            </Link>
            <div className="grid gap-6" style={{ gridTemplateColumns: "1.7fr 1fr" }}>
                {/* Left thread */}
                <div className="lt-card" style={{ padding: 28 }}>
                    <div className="flex items-start justify-between gap-3" style={{ marginBottom: 12, flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <h1 className="lt-h2" data-testid="ticket-detail-title">{t.title}</h1>
                            <p style={{ fontSize: 13, color: "#8A8F88", margin: "6px 0 0 0" }}>
                                Aperto il {fmtDateTime(t.created_at)} da {t.opened_by?.name || "—"} · {t.client?.company_name}
                            </p>
                        </div>
                        <StatusChip status={t.status} />
                    </div>

                    <div className="flex flex-col gap-3" style={{ marginTop: 16 }}>
                        {t.messages?.map((m) => (
                            <div key={m.id} className={m.author_user_type === "internal" ? "lt-msg-internal" : "lt-msg-client"} data-testid={`msg-${m.id}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="lt-msg-author">{m.author_name}</span>
                                        <span style={{ fontSize: 12, color: "#8A8F88", marginLeft: 8 }}>
                                            {m.author_user_type === "internal" ? "ENERLEFT" : t.client?.company_name}
                                        </span>
                                    </div>
                                    <span style={{ fontSize: 12, color: "#8A8F88" }}>{fmtDateTime(m.created_at)}</span>
                                </div>
                                <div className="lt-msg-body">{m.text}</div>
                                <AttachmentBubble attachments={m.attachments} />
                            </div>
                        ))}
                    </div>

                    <form onSubmit={reply} style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #E3E5E1" }} data-testid="reply-form">
                        <label className="lt-label">Risposta</label>
                        <textarea className="lt-field" rows={4} value={text} onChange={(e) => setText(e.target.value)} required data-testid="reply-text" />
                        <AttachmentPicker files={files} onChange={setFiles} disabled={sending} testIdPrefix="reply-attach" />
                        <div className="lt-radio-row" data-testid="reply-status-options">
                            {radioOpts.map((o) => (
                                <button type="button" key={o.k}
                                    className={"lt-radio-chip " + ((setStatus || "keep") === o.k ? "is-active" : "")}
                                    onClick={() => setSetStatus(o.k)} data-testid={`reply-opt-${o.k}`}>{o.l}</button>
                            ))}
                        </div>
                        <button className="lt-btn-primary" type="submit" disabled={sending} data-testid="reply-submit">
                            {sending && files.length > 0 ? "Caricamento allegati…" : "Pubblica risposta"}
                        </button>
                    </form>
                </div>

                {/* Right gestione */}
                <div className="lt-card" style={{ padding: 28 }} data-testid="ticket-manage-panel">
                    <h2 className="lt-section-title">Gestione</h2>

                    <label className="lt-label">Stato</label>
                    <div className="flex items-center gap-2">
                        <select className="lt-field" style={{ marginBottom: 0 }} value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)} data-testid="manage-status-select">
                            <option value={t.status}>{STATUS_LABELS[t.status]} (corrente)</option>
                            {allowedNext.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                        </select>
                        <button className="lt-btn-secondary" onClick={changeStatus} data-testid="manage-status-apply">Applica</button>
                    </div>

                    <label className="lt-label" style={{ marginTop: 16 }}>Categoria</label>
                    {editCat ? (
                        <div className="flex items-center gap-2">
                            <select className="lt-field" style={{ marginBottom: 0 }} value={catDraft} onChange={(e) => setCatDraft(e.target.value)}>
                                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                            <button className="lt-btn-secondary" onClick={saveCat}>Salva</button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <span style={{ flex: 1, color: "#181B20", fontSize: 14 }}>{t.category?.name || "—"}</span>
                            <button className="lt-btn-ghost" onClick={() => setEditCat(true)}>Modifica</button>
                        </div>
                    )}

                    <label className="lt-label" style={{ marginTop: 16 }}>Assegnato a</label>
                    <div className="flex items-center gap-2">
                        <select className="lt-field" style={{ marginBottom: 0 }} value={assigneeDraft} onChange={(e) => setAssigneeDraft(e.target.value)} data-testid="manage-assignee-select">
                            <option value="">Non assegnato</option>
                            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                        <button className="lt-btn-secondary" onClick={assign} data-testid="manage-assignee-apply">Assegna</button>
                    </div>

                    <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #E3E5E1", fontSize: 13, color: "#5F6560" }}>
                        <div>Cliente: <Link to={`/app/clienti/${t.client?.id}`} className="lt-link" style={{ color: "#181B20" }}>{t.client?.company_name}</Link></div>
                        <div style={{ marginTop: 6 }}>Aperto il: {fmtDateTime(t.created_at)}</div>
                        {t.closed_at && <div style={{ marginTop: 6 }}>Chiuso il: {fmtDateTime(t.closed_at)}</div>}
                    </div>

                    {canDelete && (
                        <div
                            style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #E3E5E1" }}
                            data-testid="ticket-danger-zone"
                        >
                            <label className="lt-label" style={{ color: "#B91C1C" }}>Zona pericolo</label>
                            <button
                                type="button"
                                onClick={() => setConfirmDelete(true)}
                                data-testid="ticket-delete-btn"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "8px 14px",
                                    background: "transparent",
                                    color: "#B91C1C",
                                    border: "1px solid #B91C1C",
                                    borderRadius: 2,
                                    fontFamily: '"IBM Plex Mono", monospace',
                                    fontSize: 12,
                                    letterSpacing: "0.05em",
                                    textTransform: "uppercase",
                                    cursor: "pointer",
                                }}
                            >
                                <Trash2 size={14} /> Elimina
                            </button>
                            <p style={{ fontSize: 12, color: "#8A8F88", marginTop: 8, marginBottom: 0 }}>
                                Riservato al super admin. Il ticket verrà rimosso dalle liste.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {confirmDelete && (
                <div
                    className="lt-modal-overlay"
                    data-testid="ticket-delete-modal"
                    onClick={(e) => e.target === e.currentTarget && !deleting && setConfirmDelete(false)}
                >
                    <div className="lt-modal" style={{ maxWidth: 480 }}>
                        <h2 className="lt-section-title" style={{ marginBottom: 12 }}>Eliminare il ticket?</h2>
                        <p style={{ fontSize: 14, color: "#3A3F3A", marginBottom: 20 }}>
                            Eliminare definitivamente il ticket <strong>{t.title}</strong>?
                            L&apos;azione non è reversibile dall&apos;interfaccia.
                        </p>
                        <div className="flex items-center justify-end" style={{ gap: 8 }}>
                            <button
                                type="button"
                                className="lt-btn-secondary"
                                onClick={() => setConfirmDelete(false)}
                                disabled={deleting}
                                data-testid="ticket-delete-cancel"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={doDelete}
                                disabled={deleting}
                                data-testid="ticket-delete-confirm"
                                style={{
                                    padding: "10px 18px",
                                    background: "#B91C1C",
                                    color: "#FFFFFF",
                                    border: "none",
                                    borderRadius: 2,
                                    fontFamily: '"IBM Plex Mono", monospace',
                                    fontSize: 12,
                                    letterSpacing: "0.05em",
                                    textTransform: "uppercase",
                                    cursor: deleting ? "wait" : "pointer",
                                    opacity: deleting ? 0.7 : 1,
                                }}
                            >
                                {deleting ? "Eliminazione…" : "Elimina"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
